<template>
  <div>
    <div v-if="loading || isLoading">
      <q-markup-table
        flat
        class="q-mx-xs q-mb-xs"
      >
        <thead>
          <tr>
            <th
              class="text-left"
              style="width: 25vw"
            >
              <q-skeleton
                type="text"
              />
            </th>
            <th class="text-right">
              <q-skeleton
                type="text"
              />
            </th>
            <th class="text-right">
              <q-skeleton
                type="text"
              />
            </th>
            <th class="text-right">
              <q-skeleton
                type="text"
              />
            </th>
            <th class="text-right">
              <q-skeleton
                type="text"
              />
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="n in 3"
            :key="n"
          >
            <td class="text-left">
              <q-skeleton
                type="text"
                width="85px"
              />
            </td>
            <td class="text-right">
              <q-skeleton
                type="rect"
                width="40px"
              />
            </td>
            <td class="text-right">
              <q-skeleton
                type="rect"
                width="50px"
              />
            </td>
            <td class="text-right">
              <q-skeleton
                type="rect"
                width="50px"
              />
            </td>
            <td class="text-right">
              <q-skeleton
                type="rect"
                width="50px"
              />
            </td>
          </tr>
        </tbody>
      </q-markup-table>
    </div>
    <div
      v-else-if="!hasData"
      class="row full-width q-pl-md no-data-title items-center"
    >
      <behavior-tracking-icon
        v-if="isBehaviorTracking"
        size="xs"
      />
      {{ programName }}
      <q-btn
        v-if="program"
        icon="info"
        size="sm"
        round
        flat
        dense
        color="dark-variant"
        class="q-mx-sm"
        @click="onShowInfo"
      >
        <q-tooltip>{{ programName }}</q-tooltip>
      </q-btn>
    </div>
    <q-table
      v-else-if="hasDataOrNotes"
      :columns="columns"
      :rows="data"
      :rows-per-page-options="[0]"
      row-key="id"
      :pagination="pagination"
      hide-pagination
      flat
      dense
      class="full-width sticky-col"
    >
      <template #header="props">
        <q-tr v-if="programHasMode">
          <q-th
            v-for="programModeHeader in programModeHeaders"
            :key="programModeHeader.id"
            :colspan="programModeHeader.count"
            :class="programModeHeader.color"
            class="text-center"
          >
            {{ programModeHeader.label }}
          </q-th>
        </q-tr>
        <q-tr :props="props">
          <q-th
            v-for="col in props.cols"
            :key="col.name"
            :class="getProgramModeBgColor(col)"
            :props="props"
          >
            <template
              v-if="col.name === 'targetLabel'"
            >
              <div class="row items-start no-wrap">
                <span class="target-label-truncate inline-block">
                  <behavior-tracking-icon
                    v-if="isBehaviorTracking"
                    size="xs"
                  />{{ col.label }}
                </span>
                <q-btn
                  v-if="program"
                  icon="info"
                  size="sm"
                  round
                  flat
                  dense
                  color="dark-variant"
                  class="q-mx-sm"
                  @click="onShowInfo"
                >
                  <q-tooltip>{{ programName }}</q-tooltip>
                </q-btn>
                <q-badge
                  v-if="isFrequencyData"
                  outline
                  color="green"
                  class="q-ml-xs"
                  :class="editMouseCursorClass"
                >
                  {{ getDurationBadge(props.row) }}
                  <q-popup-edit
                    v-if="edit"
                    v-slot="scope"
                    v-model="observationDuration"
                    auto-save
                  >
                    <q-input
                      v-model="scope.value"
                      mask="#:##:##"
                    />
                  </q-popup-edit>
                </q-badge>
              </div>
            </template>
            <template v-else>
              {{ col.label }}
            </template>
          </q-th>
        </q-tr>
      </template>
      <template #body="props">
        <q-tr :props="props">
          <q-td
            key="targetLabel"
            :props="props"
            :class="{
              'cursor-pointer' : !edit,
              ['sub-target-'+props.row.indent]: props.row.indent > 0,
            }"
            @click="onClickTarget(props.row.id)"
          >
            <div
              v-if="showDiscriminativeStimulus && props.row.discriminativeStimulus"
              class="dark-variant text-italic"
            >
              {{ props.row.discriminativeStimulus }}
            </div>
            {{ props.row.targetLabel }}
            <status-chip
              :target-status="targetStatusBadge(props.row.id)"
              show-arrow
              transition="animated bounceIn slower delay-1s"
              size="xs"
              :statuses="getTargetWorkflow(props.row.target)"
            />
          </q-td>
          <q-td
            key="aggregatedData"
            :props="props"
            class="row items-center justify-end no-wrap"
          >
            <template
              v-if="showBelowMinTrialsBadge(props.row)"
            >
              <universal-tooltip
                :show-tooltip="true"
                :text="$t('sessions.trialOverview.belowMinTrialsTooltip')"
                class="q-mr-sm"
              >
                <q-badge> {{ $t('sessions.trialOverview.belowMinTrialsLabel') }}</q-badge>
              </universal-tooltip>
            </template>
            <template
              v-if="showTargetBadge(props.row)"
            >
              <q-btn
                v-if="canDeleteTargetData(props.row)"
                icon="delete"
                size="xs"
                flat
                round
                color="negative"
                class="q-mr-xs"
                @click="onClearTargetData(props.row.id)"
              >
                <q-tooltip>{{ $t('sessions.trialOverview.tooltipClearData') }}</q-tooltip>
              </q-btn>
              <q-badge
                color="green"
                class="cursor-pointer non-selectable"
                data-e2e="badge-status-trials"
                @click="onSwitchBadgeFormat(props.row)"
              >
                <q-icon
                  v-if="getBadgeIcon(props.row)"
                  :name="getBadgeIcon(props.row)"
                  color="white"
                  class="q-mr-xs"
                />
                {{ getBadge(props.row) }}
                <q-tooltip v-if="!isBadgeLabelMissing(props.row)">
                  {{ getBadgeLabel(props.row) }}
                </q-tooltip>
              </q-badge>
            </template>
          </q-td>
          <!--
            Exactly one q-td per column, and nothing else inside the loop — not a v-if/v-else pair, not a
            comment. QTd finds its column by its vnode key, which Vue hands down only to a sole child.
          -->
          <template
            v-for="col in dataColumns"
            :key="col.name"
          >
            <q-td
              :props="props"
              :style="getStyleForTrialData(props.row.trials[col.trial - 1])"
              :class="[
                getClassForTrialData(
                  props.row.trials[col.trial - 1],
                  props.row.parentTargetDataType,
                  col.isTaskAnalysisTrial,
                  props.row
                ),
                getClassForOccurrence(col.occurrenceSlot, props.row),
              ]"
              :data-e2e="isOccurrenceRow(col.occurrenceSlot, props.row) ? 'cell-behavior-occurrence' : null"
              @click="onClickData(props.row, col.trial)"
            >
              <q-tooltip v-if="isOccurrenceRow(col.occurrenceSlot, props.row)">
                <span v-html="getOccurrenceTooltip(col.occurrenceSlot)" />
              </q-tooltip>
              <q-menu
                v-if="showEditMenu(props.row.trials[col.trial - 1],
                                   props.row.parentTargetDataType,
                                   col.isTaskAnalysisTrial,
                                   props.row)
                  && (!hasMultipleDataEntries(props.row.trials[col.trial - 1]))"
                :ref="'editMenu_' + props.row.id + '_' + col.trial"
                touch-position
              >
                <div
                  class="q-pa-xs text-center text-subtitle2 bg-primary text-white"
                >
                  {{ $t('sessions.trialOverview.edit') }}
                </div>
                <q-separator />
                <q-list style="min-width: 100px">
                  <q-item
                    v-if="isDurationTarget(props.row)"
                  >
                    <q-item-section
                      class="row"
                    >
                      <q-input
                        dense
                        :model-value="formatDuration(props.row.trials[col.trial - 1].duration)"
                        :label="$t('sessions.trialOverview.durationInputLabel')"
                        mask="#:##:##"
                        autofocus
                        @change="onEditDataDuration(
                          $event,
                          props.row.trials[col.trial - 1].trial,
                          props.row.trials[col.trial - 1].target,
                        )"
                        @keyup.enter="onSubmitDataDuration(
                          'editMenu_' + props.row.id + '_' + col.trial
                        )"
                      >
                        <template #append>
                          <q-btn
                            icon="done"
                            flat
                            round
                            @click="onSubmitDataDuration(
                              'editMenu_' + props.row.id + '_' + col.trial
                            )"
                          />
                        </template>
                      </q-input>
                    </q-item-section>
                  </q-item>
                  <template
                    v-if="!isDurationTarget(props.row) || hasMultiplePromptLevels(props.row)"
                  >
                    <q-item
                      v-for="promptLevel in getPromptLevels(props.row)"
                      :key="promptLevel.id"
                      v-close-popup
                      clickable
                      :style="getPromptLevelStyle(promptLevel)"
                      class="prompt-level"
                      @click="onEditDataPromptLevel(
                        props.row.trials[col.trial - 1],
                        promptLevel,
                        props.row.id,
                        col.trial,
                        props.row.parentTargetDataType,
                        col.isTaskAnalysisTrial
                      )"
                    >
                      <q-item-section
                        :class="getEditPromptItemClass(
                          promptLevel,
                          props.row.trials[col.trial - 1]
                        )"
                      >
                        {{ promptLevel.trialOutcome }}
                      </q-item-section>
                    </q-item>
                  </template>
                  <template
                    v-if="canDelete(props.row.trials[col.trial - 1])"
                  >
                    <q-separator />
                    <q-item
                      v-close-popup
                      clickable
                      class="text-negative"
                      @click="onDeleteData(props.row.id, props.row.trials[col.trial - 1])"
                    >
                      <q-item-section>
                        {{ $t('sessions.trialOverview.deleteEntry') }}
                      </q-item-section>
                    </q-item>
                  </template>
                </q-list>
              </q-menu>
              <q-tooltip
                v-if="!edit && hasTrialData(props.row.trials, col.trial - 1)"
                @before-show="onShowTooltip(props.row, col.trial)"
              >
                <q-spinner v-if="currentTooltip === null" />
                <span
                  v-else
                  v-html="currentTooltip"
                />
              </q-tooltip>
            </q-td>
          </template>

          <q-td
            v-if="edit"
            v-ripple
            class="nostretch"
            :class="{ 'cursor-pointer': canAddData(props.row) }"
            @click="onClickAdd(props.row)"
          >
            <template v-if="canAddData(props.row)">
              <q-icon
                v-if="edit"
                v-touch-hold.mouse="onAddMultipleTrials"
                name="add_task"
                color="primary-variant"
                size="xs"
                class="q-mr-xs"
                :data-target-id="props.row.id"
              />
              <q-menu
                v-if="hasPromptLevelAddDataMenu(props.row)"
                :ref="'addMenu_' + props.row.id"
                touch-position
              >
                <div
                  class="q-pa-xs text-center text-subtitle2 bg-primary text-white"
                >
                  {{ $t('sessions.trialOverview.add') }}
                </div>
                <q-separator />
                <q-item
                  v-if="isDurationTarget(props.row)"
                >
                  <q-item-section
                    class="row"
                  >
                    <q-input
                      v-model="addDurationTime"
                      dense
                      label="Duration"
                      mask="#:##:##"
                      autofocus
                      @keyup.enter.stop="onAddDataDuration(
                        props.row,
                        'addMenu_' + props.row.id
                      )"
                    >
                      <template
                        v-if="!hasMultiplePromptLevels(props.row)"
                        #append
                      >
                        <q-btn
                          icon="done"
                          flat
                          round
                          @click.stop="onAddDataDuration(
                            props.row,
                            'addMenu_' + props.row.id
                          )"
                        />
                      </template>
                    </q-input>
                  </q-item-section>
                </q-item>
                <q-list
                  v-if="hasMultiplePromptLevels(props.row)"
                  style="min-width: 100px"
                >
                  <q-item
                    v-for="promptLevel in getPromptLevels(props.row)"
                    :key="promptLevel.id"
                    v-close-popup
                    clickable
                    :style="getPromptLevelStyle(promptLevel)"
                    class="prompt-level"
                    @click="onAddData(props.row, promptLevel)"
                  >
                    <q-item-section>{{ promptLevel.trialOutcome }}</q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
              <q-menu
                v-if="$q.platform.is.desktop"
                touch-position
                context-menu
              >
                <q-list dense>
                  <q-item
                    v-close-popup
                    clickable
                  >
                    <q-item-section
                      @click="onAddMultipleTrials(props.row.id)"
                    >
                      {{ $t('sessions.trialOverview.modals.addMultipleTrials.title') }}
                    </q-item-section>
                  </q-item>
                </q-list>
              </q-menu>
            </template>
          </q-td>
        </q-tr>
      </template>
      <template #no-data />
    </q-table>
    <template
      v-if="hasDataOrNotes"
    >
      <div
        class="full-width q-mt-xs q-mb-md"
        :class="editMouseCursorClass"
      >
        <q-input
          v-if="hasProgramNotes || edit"
          :model-value="programNotes"
          :label="$t('run.collectData.inputProgramNotesLabel')"
          outlined
          autogrow
          hide-bottom-space
          dense
          class="full-width"
          :readonly="!edit"
          @update:model-value="onInputProgramNotes"
        />
      </div>
      <div
        v-if="hasSupervisionNotes"
        class="full-width q-mt-xs q-mb-md"
      >
        <div
          v-for="supervisorNote in filteredDataNotes"
          :key="supervisorNote.user"
        >
          <div class="text-dark-variant q-pl-md">
            {{ supervisionNotesTitle(supervisorNote.userName) }}
          </div>
          <text-editor
            v-model="supervisorNote.supervisionNotes"
            tables
            indent
            :disabled="!edit"
            wrapper-class="q-mb-md"
            class="full-width lower-height"
            @update:model-value="onInputSupervisionNotes($event, supervisorNote.sessionActivity)"
          />
        </div>
      </div>
    </template>
    <div
      v-else
      class="full-width text-center my.lg"
    >
      {{ noDataMessage }}
    </div>
  </div>
</template>
<script>
import { measurementMetrics, programTypes } from 'boot/globals';
import AddMultipleTrials from 'components/Modals/AddMultipleTrials';
import BehaviorTrackingIcon from 'components/Programs/BehaviorTrackingIcon';
import ProgramInfo from 'components/Programs/ProgramInfo';
import StatusChip from 'components/Programs/StatusChip';
import TargetDetails from 'components/Programs/TargetDetails';
import TextEditor from 'components/UI/TextEditor';
import UniversalTooltip from 'components/UI/UniversalTooltip.vue';
import {
  chain, concat, fill,
  find, forEach, get, head, isArray, isEmpty, isObject, isString, isUndefined, map, pick,
  range, reduce, size,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { colors } from 'quasar';
import ProgramsDB from 'src/fb/programs-db';
import Auth from 'src/mixins/Auth';
import Data from 'src/mixins/Data';
import Interactions from 'src/mixins/Interactions';
import Targets from 'src/mixins/Targets';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { programsTargets } from 'src/store/programsTargets';
import { promptLevels } from 'src/store/promptLevels';
import { workflows } from 'src/store/workflows';
import { PROGRAM_TYPE_SKILL_ACQUISITION } from 'src/constants/programs';
import {
  interleaveOccurrenceColumns,
  isBehaviorOccurrence,
  toOccurrenceSlots,
} from 'src/utils/behaviorOccurrences';
import { escapeHtml } from 'src/utils/HtmlUtils';
import { logEvent } from 'src/utils/LogEvent';
import { isBehaviorTrackingProgram } from 'src/utils/ProgramUtils';
import { formatRate, getTargetType } from 'src/utils/TargetUtils';
import { OUTCOME_MEASUREMENT_TYPES } from 'src/constants/prompt-level-template';
import { formatRatingScore } from 'src/utils/PromptLevelUtils';

export default {
  name: 'TrialOverview',

  components: {
    BehaviorTrackingIcon, UniversalTooltip, StatusChip, TextEditor,
  },

  mixins: [
    Auth,
    Data,
    Interactions,
    Toolbox,
    Targets,
  ],

  props: {
    sessionProgram: {
      type: Object,
      required: true,
    },

    program: {
      type: Object,
      required: true,
    },

    session: {
      type: Object,
      required: true,
    },

    dataNotes: {
      type: Array,
      default: () => ([]),
    },

    edit: {
      type: Boolean,
      default: false,
    },

    time: {
      type: Number,
      required: true,
    },

    fromTime: {
      type: Number,
      default: 0,
    },

    isLoading: {
      type: Boolean,
      default: false,
    },

    noDataMessage: {
      type: String,
      default: '',
    },

    showDiscriminativeStimulus: {
      type: Boolean,
      default: false,
    },

    canShowInfo: {
      type: Boolean,
      default: true,
    },

    isWorkflowsPreloaded: {
      type: Boolean,
      default: false,
    },

    isPreloadComplete: {
      type: Boolean,
      default: false,
    },

    /** Programs in the same session, where a behavior occurrence's skill target is looked up. */
    sessionProgramIds: {
      type: Array,
      default: () => ([]),
    },

    /**
     * Every behavior occurrence in the session, each carrying `behaviorProgram` and
     * `behaviorProgramName`. A Skill Acquisition grid shows those attached to its own targets.
     */
    sessionBehaviorOccurrences: {
      type: Array,
      default: () => ([]),
    },

  },

  emits: ['data-notes-changed', 'program-notes-changed', 'data-changed', 'seek'],

  data() {
    return {
      currentSessionProgram: {},

      badgeMetrics: {},
      currentTooltip: null,

      pagination: {
        rowsPerPage: 1000,
        // rowsNumber: xx if getting data from a server
      },
      columns: [],
      data: [],

      /** Skill target labels by id, for the behavior occurrences in this program's data. */
      attachedTargetLabels: {},

      programModeHeaders: [],

      addDurationTime: '0:00:00',

      loading: true,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails']),
    ...mapState(promptLevels, ['promptLevelTemplates', 'activePromptLevels']),
    ...mapState(workflows, ['workflows', 'workflowStatuses']),

    isFrequencyData() {
      return get(this.currentSessionProgram, ['data', 'dataType']) === 'Frequency1.0';
    }, // isFrequencyData()

    isTaskAnalysisProgram() {
      return get(this.currentSessionProgram, ['data', 'dataType']) === 'Task1.0';
    }, // isTaskAnalysisProgram()

    isIntervalProgram() {
      return get(this.currentSessionProgram, ['data', 'dataType']) === 'Interval1.0';
    }, // isIntervalProgram()

    hasData() {
      return !isEmpty(this.data);
    }, // hasData()

    programHasMode() {
      return get(programTypes, [this.program.type, 'hasModes'], false);
    },

    hasProgramNotes() {
      return !!this.programNotes;
    }, // hasProgramNotes()

    hasSupervisionNotes() {
      return !!this.filteredDataNotes.length;
    }, // hasSupervisionNotes()

    hasDataOrNotes() {
      return this.hasData || this.hasProgramNotes || this.hasSupervisionNotes;
    }, // hasDataOrNotes()

    programNotes() {
      return get(this.sessionProgram.data, 'notes');
    }, // programNotes()

    filteredDataNotes() {
      return this.dataNotes.filter(({ supervisionNotes }) => supervisionNotes);
    },

    dataColumns() {
      if (this.edit) {
        return this.columns.slice(2, -1);
      }
      return this.columns.slice(2);
    },

    programId() {
      return get(this.program, 'id');
    },

    isBehaviorTracking() {
      return isBehaviorTrackingProgram(this.program);
    }, // isBehaviorTracking()

    programName() {
      return this.program?.programName || this.sessionProgram?.programName || '';
    }, // programName()

    lastTrial() {
      if (this.session.hasTimecodedData) {
        return chain(this.currentSessionProgram)
          .get(['data', 'data'])
          .filter((data) => data.time <= this.time)
          .maxBy('trial')
          .get('trial', 0)
          .value();
      }
      return chain(this.currentSessionProgram)
        .get(['data', 'data'])
        .maxBy('trial')
        .get('trial', 0)
        .value();
    },

    observationDuration: {
      get() {
        return this.formatDuration(this.currentSessionProgram.data.duration);
      },
      set(formattedDuration) {
        const duration = this.stringToDuration(formattedDuration) || 1;
        this.currentSessionProgram.data.duration = duration;
      },
    }, // observationDuration()

    editMouseCursorClass() {
      return this.edit ? 'cursor-pointer' : '';
    }, // editMouseCursorClass()

  },

  watch: {
    sessionProgram: {
      immediate: true,
      deep: false,
      async handler(sessionProgram) {
        this.currentSessionProgram = sessionProgram;
        this.loading = true;
        if (!this.isWorkflowsPreloaded) {
          await this.reloadProgramWorkflows();
        }
        await this.mapTargetsAndOutcomes();
        this.loading = false;
      },
    },

    edit: {
      immediate: false,
      handler() {
        this.mapTargetsAndOutcomes();
      },
    },

    isPreloadComplete: {
      immediate: false,
      handler(val) {
        if (val) {
          this.mapTargetsAndOutcomes();
        }
      },
    },

    'program.id': {
      handler() {
        // update data when deleted programs are loaded
        this.mapTargetsAndOutcomes();
      },
    },

    'session.duration': {
      handler() {
        if (this.data.some((row) => (row.dataType === 'rate') && (row.timerType === 'session'))) {
          this.mapTargetsAndOutcomes();
        }
      },
    },

  },

  methods: {
    ...mapActions(programsTargets, ['getAllProgramTargets']),
    ...mapActions(workflows, ['reloadWorkflows']),

    checkIfTargetIsRating(target) {
      const template = this.targetPromptLevelTemplate(target);
      return template?.outcomeMeasurement === OUTCOME_MEASUREMENT_TYPES.RATING_SCALE;
    }, // checkIfTargetIsRating()

    onInputSupervisionNotes(supervisionNotes, sessionActivity) {
      this.$emit('data-notes-changed', { supervisionNotes, sessionActivity });
    },

    onInputProgramNotes(programNote) {
      this.$emit('program-notes-changed', { programNote });
    }, // onInputProgramNotes()

    onShowTooltip(target, trial) {
      const trialInfo = target.trials[trial - 1];
      let time = 0;
      let duration = null;

      if (isArray(trialInfo)) {
        time = (chain(trialInfo).minBy('time').get('time').value());
        duration = chain(trialInfo).sumBy('duration').value();
      } else {
        time = trialInfo.time;
      }
      let tooltip = `Trial: #${trial}`;
      if (time > 0) {
        tooltip += ` (${this.formatDuration(time)})`;
      }
      tooltip += '<br />';
      tooltip += `${target.targetLabel}<br />`;
      if (isBehaviorOccurrence(trialInfo)) {
        const trialOutcome = trialInfo.promptLevelDetails?.trialOutcome;
        if (trialOutcome) {
          tooltip += `${escapeHtml(trialOutcome)}<br />`;
        }
        const duringTarget = this.attachedTargetLabels[trialInfo.attachedToTarget];
        if (duringTarget) {
          const occurredDuring = this.$t('sessions.trialOverview.textOccurredDuring', { target: duringTarget });
          tooltip += `${escapeHtml(occurredDuring)}<br />`;
        }
      } else if (!isArray(trialInfo)) {
        tooltip += `${get(trialInfo.promptLevelDetails, 'trialOutcome', '')}<br />`;
      }
      if (this.isDurationTarget(target)) {
        duration = trialInfo.duration;
      }
      if (duration !== null) {
        tooltip += `${this.$t('sessions.trialOverview.durationInputLabel')}: ${
          this.formatDuration(duration)}`;
      }
      if ((isArray(trialInfo))) {
        const promptLevelResults = chain(trialInfo)
          .filter('promptLevelDetails')
          .map((filteredTrial) => pick(filteredTrial, ['promptLevel', 'promptLevelDetails']))
          .groupBy('promptLevel')
          .map((promptLevelTrials) => ({
            label: chain(promptLevelTrials).head().get(['promptLevelDetails', 'trialOutcome'], '?').value(),
            trials: size(promptLevelTrials),
            order: chain(promptLevelTrials).head().get(['promptLevelDetails', 'order'], 99999).value(),
          }))
          .sortBy('order')
          .map((promptLevel) => (`<tr><td>${promptLevel.label}</td><td>${promptLevel.trials}</td></tr>`))
          .join('')
          .value();

        const successes = chain(trialInfo).filter({ scoreValue: 100 }).size().value();
        const rate = formatRate(successes, duration, this.getBadgeMetric(target.target))
          ?? this.formatFrequency(successes, duration);
        tooltip += `<br/>&nbsp;<br />${rate}`;
        tooltip += `<br/>&nbsp;<br /><table class="tooltip-table"><tbody>${promptLevelResults}</tbody></table>`;
      }

      this.currentTooltip = tooltip;
    }, // onShowTooltip()

    onHideTooltip() {
      this.currentTooltip = null;
    }, // onHideTooltip()

    onClickData(target, trial) {
      if (this.edit && isArray(target.trials[trial - 1]) && (!isEmpty(target.trials[trial - 1]))) {
        this.editRateTrials(target, trial);
      }
      const trialTime = isArray(target.trials[trial - 1])
        ? chain(target.trials[trial - 1]).minBy('time').get('time', -1).value()
        : get(target.trials[trial - 1], 'time', -1);

      if (trialTime > 0) {
        this.jumpToTimestamp(trialTime);
      }
    }, // onClickData()

    isPromptLevelTemplateDeleted(promptLevelTemplate) {
      return promptLevelTemplate && !this.getActivePromptLevelTemplate(promptLevelTemplate);
    }, // isPromptLevelTemplateDeleted()

    hasPromptLevelAddDataMenu(target) {
      return this.edit
        && ((this.hasMultiplePromptLevels(target)) || this.isDurationTarget(target))
        && ((target.dataType !== 'rate') || (target.timerType === 'session'));
    }, // hasPromptLevelAddDataMenu()

    isDurationTarget(target) {
      return (get(this.program, 'type') === 'duration')
        || (get(target, 'dataType') === 'duration');
    }, // isDurationTarget()

    isRateTarget(target) {
      return get(target, 'dataType') === 'rate';
    }, // isRateTarget()

    targetStatusBadge(targetId) {
      return chain(this.session)
        .get('targetStatusChanges')
        .find({ program: this.sessionProgram.program, target: targetId })
        .get('toStatus')
        .value();
    }, // targetStatusBadge()

    exportData(format = 'csv') {
      switch (format) {
        case 'csv':
        default:
          return this.exportCSV();
      }
    }, // exportData()

    exportCSV() {
      const dataType = get(this.currentSessionProgram, ['data', 'dataType']);
      if (dataType === 'Task1.0') {
        const targetName = '';
        const trials = '';
        let successRate = '';
        const numSuccesses = chain(this.data)
          .reduce((pV, target) => pV + target.aggregatedData.successes, 0)
          .value();

        const numTrials = chain(this.data)
          .reduce((pV, target) => pV + target.aggregatedData.trials, 0)
          .value();

        if (numTrials > 0) {
          successRate = `${Math.round((numSuccesses / numTrials) * 100)}%`;
        }

        return `${this.programName}\t${targetName}\t${trials}\t${successRate}`;
      }

      return chain(this.data)
        .map((target) => {
          const targetName = target.targetLabel;
          let trials = '';

          const promptLevelTemplate = get(this.currentSessionProgram, ['data', 'promptLevelTemplate'], '');

          const promptLevelCount = chain(this.activePromptLevels)
            .find({ id: promptLevelTemplate })
            .get('promptLevels')
            .size()
            .value();

          if (promptLevelCount > 1) {
            trials = chain(target)
              .get('trials')
              .map((trial) => get(trial, ['promptLevelDetails', 'trialOutcome']))
              .compact()
              .join(' ')
              .value();
          }

          if (this.checkIfTargetIsRating(target)) {
            const successRate = formatRatingScore(target.aggregatedData?.percentage);
            return `${this.programName}\t${targetName}\t${trials}\t${successRate}`;
          }

          let successRate = '';

          const dataTypeInfo = target.dataType || get(this.program, 'type');
          switch (dataTypeInfo) {
            case 'rate':
              successRate = `${target.aggregatedData.successes}`;
              break;
            case 'frequency':
              if (get(this.currentSessionProgram, ['data', 'duration']) > 0) {
                successRate = `${target.aggregatedData.successes}`;
              }
              break;
            case 'duration':
              successRate = `${this.formatDuration(target.aggregatedData.successfulDuration)}`;
              break;
            default: {
              if (target.aggregatedData.trials > 0) {
                successRate = `${Math.round((target.aggregatedData.successes / target.aggregatedData.trials) * 100)}%`;
              }
            }
          }

          return `${this.programName}\t${targetName}\t${trials}\t${successRate}`;
        })
        .join('\n')
        .value();
    }, // exportCSV()

    hasMultipleDataEntries(trial) {
      return isArray(trial);
    }, // hasMultipleDataEntries()

    showEditMenu(trial, parentTargetDataType, isTaskAnalysisTrial, target) {
      if (!this.edit) {
        return false;
      }
      if (target && !this.canAddData(target)) {
        return false;
      }
      if (this.isTaskAnalysisProgram
        || (isTaskAnalysisTrial && (parentTargetDataType === 'taskAnalysis'))
        || this.isIntervalProgram
      ) {
        // Task analysis data can contain multiple targets per trial
        return true;
      }

      return !isEmpty(trial);
    }, // showEditMenu()

    async onClickTarget(targetId) {
      if (!this.edit) {
        this.onShowTargetDetails(targetId);
      }
    }, // onClickTarget()

    onClickAdd(target) {
      if (this.isPromptLevelTemplateDeleted(get(this.program, 'promptLevelTemplate'))) {
        this.notifyWarning(this.$t('run.collectData.invalidPromptLevelTemplateSelected'));
        return;
      }

      if (this.isPromptLevelTemplateDeleted(get(target, 'promptLevelTemplate'))) {
        this.notifyWarning(this.$t('sessions.trialOverview.notifyDeletedPromptLevelTemplate'));
      }

      if (this.edit) {
        if ((target.dataType === 'rate') && (target.timerType !== 'session')) {
          this.addRateTrials(target);
          return;
        }
        if ((!this.hasMultiplePromptLevels(target)) && (!this.isDurationTarget(target))) {
          this.onAddData(target, head(this.getPromptLevels(target)));
        }
      }
    }, // onClickAdd()

    async onAddData(target, promptLevel) {
      if (
        (
          this.isDurationTarget(target)
          || this.isFrequencyData
          || ((target.dataType === 'rate') && (target.timerType === 'session'))
        )
      && (chain(this.currentSessionProgram)
        .get(['data', 'data'])
        .find({ target: target.id })
        .isUndefined()
        .value())) {
        // No data initialized for this target
        const insertData = { target: target.id };

        if ((target.dataType === 'rate') && (target.timerType === 'session')) {
          insertData.durationFromSession = true;
        }

        this.currentSessionProgram.data.data.splice(0, 0, insertData);
      }

      const parentTarget = chain(this.data).find({ id: target.id }).get('parentTarget', null).value();
      const data = {
        promptLevel: promptLevel.id,
        scoreValue: promptLevel.score,
        target: target.id,
        trial: this.lastTrial + 1,
        parentTarget,
        ...(this.checkIfTargetIsRating(target) ? { isRatingScale: true } : {}),
      };

      if (this.session.hasTimecodedData) {
        data.time = this.time;
      }
      if (this.isDurationTarget(target)) {
        data.duration = this.stringToDuration(this.addDurationTime);
        this.addDurationTime = '0:00:00';
      }
      if (this.currentSessionProgram.data.promptLevelTemplate !== target.promptLevelTemplate) {
        // Individual prompt level template per target
        data.promptLevelTemplate = target.promptLevelTemplate;
      }
      this.pushData(data);
      this.mapTargetsAndOutcomes();
    }, // onAddData()

    onAddMultipleTrials(evtData) {
      if (this.isPromptLevelTemplateDeleted(get(this.program, 'promptLevelTemplate'))) {
        this.notifyWarning(this.$t('run.collectData.invalidPromptLevelTemplateSelected'));
        return;
      }

      // we can't pass params directly from v-touch-hold, so parse an event object instead
      const targetId = isString(evtData) ? evtData : evtData.evt.target.dataset.targetId;

      const target = find(this.data, { id: targetId });
      if ((target.dataType === 'rate') && (target.timerType !== 'session')) {
        return;
      }

      if (this.isPromptLevelTemplateDeleted(get(target, 'promptLevelTemplate'))) {
        this.notifyWarning(this.$t('sessions.trialOverview.notifyDeletedPromptLevelTemplate'));
      }

      this.openComponentDialog(
        AddMultipleTrials,
        {
          program: this.currentSessionProgram,
          promptLevelTemplate: this.targetPromptLevelTemplate(target),
          showDuration: this.isDurationTarget(target),
          duration: 0,
        },
        ({ data, duration }) => {
          const insertData = chain(data)
            .filter((promptLevelInfo) => promptLevelInfo.value > 0)
            .map((promptLevelInfo) => fill(Array(promptLevelInfo.value), {
              promptLevel: promptLevelInfo.promptLevel.id,
              scoreValue: promptLevelInfo.promptLevel.score,
              target: target.id,
              trial: null,
              time: this.time,
              parentTarget: target.parentTarget || null,
              ...(this.checkIfTargetIsRating(target) ? { isRatingScale: true } : {}),
            }))
            .flatten()
            .map((newTrial, idx) => ({
              ...newTrial,
              trial: this.lastTrial + idx + 1,
              ...(this.isDurationTarget(target) ? { duration } : {}),
            }))
            .value();

          this.currentSessionProgram.data.data = chain(this.currentSessionProgram)
            .get(['data', 'data'])
            .concat(insertData)
            .value();
          this.mapTargetsAndOutcomes();

          this.$emit('data-changed');
        },
      );
    }, // onAddMultipleTrials()

    addRateTrials(target) {
      if ((target.dataType !== 'rate') || (target.timerType === 'session')) {
        return;
      }

      this.openComponentDialog(
        AddMultipleTrials,
        {
          program: this.currentSessionProgram,
          promptLevelTemplate: this.targetPromptLevelTemplate(target),
          duration: 0,
          showDuration: true,
          allowZero: true,
          title: this.$t('common.buttonAdd'),
        },
        ({ data, duration }) => {
          const insertData = chain(data)
            .filter((promptLevelInfo) => promptLevelInfo.value > 0)
            .map((promptLevelInfo) => fill(Array(promptLevelInfo.value), {
              promptLevel: promptLevelInfo.promptLevel.id,
              scoreValue: promptLevelInfo.promptLevel.score,
              target: target.id,
              trial: this.lastTrial + 1,
              time: this.time,
              parentTarget: target.parentTarget || null,
            }))
            .flatten()
            .concat({
              target: target.id,
              trial: this.lastTrial + 1,
              parentTarget: target.parentTarget || null,
              duration,
            })
            .value();

          const adjustedData = chain(this.currentSessionProgram)
            .get(['data', 'data'])
            .concat(insertData)
            .value();

          this.currentSessionProgram.data.data = adjustedData;
          this.mapTargetsAndOutcomes();

          this.$emit('data-changed');
        },
      );
    }, // addRateTrials()

    editRateTrials(target, trial) {
      const editData = chain(target)
        .get(['trials', trial - 1])
        .filter('promptLevel')
        .countBy('promptLevel')
        .value();
      const editDuration = chain(target)
        .get(['trials', trial - 1])
        .map('duration')
        .compact()
        .sum()
        .value();

      // we can't pass params directly from v-touch-hold, so parse an event object instead
      this.openComponentDialog(
        AddMultipleTrials,
        {
          program: this.currentSessionProgram,
          promptLevelTemplate: this.targetPromptLevelTemplate(target),
          showDuration: true,
          canDelete: true,
          allowZero: true,
          data: editData,
          duration: editDuration,
          title: this.$t('common.buttonEdit'),
        },
        ({ data, duration, deleteTrial }) => {
          let adjustedData;
          if (deleteTrial) {
            adjustedData = chain(this.currentSessionProgram)
              .get(['data', 'data'])
              .reject({ target: target.id, trial })
              .value();
          } else {
            const insertData = chain(data)
              .filter((promptLevelInfo) => promptLevelInfo.value > 0)
              .map((promptLevelInfo) => fill(Array(promptLevelInfo.value), {
                promptLevel: promptLevelInfo.promptLevel.id,
                scoreValue: promptLevelInfo.promptLevel.score,
                target: target.id,
                trial,
                time: null,
                parentTarget: target.parentTarget || null,
              }))
              .flatten()
              .concat({
                target: target.id,
                trial,
                parentTarget: target.parentTarget || null,
                duration,
              })
              .value();

            adjustedData = chain(this.currentSessionProgram)
              .get(['data', 'data'])
              .reject({ target: target.id, trial })
              .concat(insertData)
              .value();
          }

          this.currentSessionProgram.data.data = adjustedData;
          this.mapTargetsAndOutcomes();

          this.$emit('data-changed');
        },
      );
    }, // editRateTrials()

    onClearTargetData(targetId) {
      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('sessions.trialOverview.dialogClearTargetMessage'),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          const adjustedData = chain(this.currentSessionProgram)
            .get(['data', 'data'])
            .reject({ target: targetId })
            .value();

          this.currentSessionProgram.data.data = adjustedData;
          this.mapTargetsAndOutcomes();

          this.$emit('data-changed');
        });
    }, // onClearTargetData()

    onSwitchBadgeFormat(target) {
      const currentBadgeMetric = this.badgeMetrics[target.id] || this.getBadgeMetric(target);
      const allowedMetrics = this.getTargetDataType(target, this.program)?.allowedMetrics;
      if (!allowedMetrics) {
        return;
      }

      const metricIdx = allowedMetrics.findIndex((metric) => metric === currentBadgeMetric);
      if (metricIdx === -1) {
        return;
      }

      this.badgeMetrics[target.id] = allowedMetrics[(metricIdx + 1) % allowedMetrics.length];
    }, // onSwitchBadgeFormat()

    pushData(insertData) {
      forEach(this.currentSessionProgram.data.data, (data, index) => {
        if (data.trial >= insertData.trial) {
          this.currentSessionProgram.data.data[index].trial += 1;
        }
      });

      try {
        this.currentSessionProgram.data.data.splice(insertData.trial - 1, 0, insertData);
      } catch (err) {
        const { id, data } = this.currentSessionProgram;
        const programId = get(this.currentSessionProgram, ['program', 'id']);
        console.error(`Can not add data to the program ${id} because of ${err}`);
        logEvent(err, {
          context: {
            insert_programdata: {
              currentSessionProgram: {
                data,
                programName: this.programName,
                programId,
                currentSessionProgramId: id,
              },
            },
          },
        });
      }

      this.$emit('data-changed');
    }, // pushData()

    onEditDataPromptLevel(data, promptLevel, targetId, trial, parentTargetDataType, isTaskAnalysisTrial) {
      if (
        isEmpty(data)
        && (
          this.isTaskAnalysisProgram
          || ((parentTargetDataType === 'taskAnalysis') && isTaskAnalysisTrial)
          || this.isIntervalProgram
        )
      ) {
        // User requested to add data to fill in a blank in a trial
        const parentTarget = chain(this.data).find({ id: targetId }).get('parentTarget', null).value();

        const insertData = {
          promptLevel: promptLevel.id,
          scoreValue: promptLevel.score,
          target: targetId,
          trial,
          time: this.time,
          parentTarget,
        };
        this.currentSessionProgram.data.data.push(insertData);
      } else {
        chain(this.currentSessionProgram)
          .get(['data', 'data'])
          .find({ target: data.target, trial: data.trial })
          .set('promptLevel', promptLevel.id)
          .set('scoreValue', promptLevel.score)
          .value();
      }

      this.mapTargetsAndOutcomes();

      this.$emit('data-changed');
    }, // onEditDataPromptLevel()

    onAddDataDuration(target, menu) {
      // Only one prompt level - add data to default prompt level with entered duration
      this.onAddData(target, head(this.getPromptLevels(target)));

      if (this.$refs[menu]) {
        this.$refs[menu].hide();
      }

      this.$emit('data-changed');
    }, // onAddDataDuration()

    onEditDataDuration(duration, trial, target) {
      chain(this.currentSessionProgram)
        .get(['data', 'data'])
        .find({ trial, target })
        .set('duration', this.stringToDuration(duration))
        .value();

      this.$emit('data-changed');
    }, // onEditDataDuration()

    onSubmitDataDuration(menu) {
      this.mapTargetsAndOutcomes();
      if (this.$refs[menu]) {
        head(this.$refs[menu]).hide();
      }
    }, // onSubmitDataDuration()

    onDeleteData(targetId, data) {
      let adjustedData = chain(this.currentSessionProgram)
        .get(['data', 'data'])
        .reject({ target: data.target, trial: data.trial })
        .value();

      if (chain(adjustedData).find({ trial: data.trial }).size().value() === 0) {
        // No more data in this trial - re-order later trials
        adjustedData = chain(adjustedData)
          .map((dataItem) => (dataItem.trial > data.trial
            ? { ...dataItem, trial: dataItem.trial - 1 }
            : dataItem))
          .value();
      }

      this.currentSessionProgram.data.data = adjustedData;
      this.mapTargetsAndOutcomes();

      this.$emit('data-changed');
    }, // onDeleteData()

    async onShowTargetDetails(targetId) {
      let program;

      if (isObject(this.currentSessionProgram.data.program)) {
        program = this.currentSessionProgram.data.program;
      } else {
        program = await this.loadProgram(this.currentSessionProgram.data.program);
      }

      this.openComponentDialog(TargetDetails, {
        program,
        targetId,
        editable: this.$route.name !== 'Session Overview'
          && this.userHasPrivilege('programs/editTargetStatus'),
      });
    },

    initData() {
      if (!this.edit || (this.currentSessionProgram.data)) {
        return;
      }
      this.currentSessionProgram.data = {
        client: this.session.client,
        program: this.programId,
        session: this.session.id,
        sessionProgram: this.currentSessionProgram.id,
        user: get(this.session, 'runBy', this.userDetails.userId),
        data: [],
        dataType: this.getDataType(this.program.type),
        notes: '',
        ...((this.program.type === 'frequency') ? { duration: this.session.duration || 0 } : {}),
      };

      if (this.program.promptLevelTemplate) {
        this.currentSessionProgram.data.promptLevelTemplate = this.program.promptLevelTemplate;
      }

      if (this.isFrequencyData) {
        // Initialize zero-count for frequency targets
        this.currentSessionProgram.data.data = map(this.data, (targetInfo) => ({ target: targetInfo.id }));
        this.$emit('data-changed');
      }
    }, // initData()

    loadProgram(programId) {
      const programsDb = new ProgramsDB();
      return programsDb.read(programId);
    }, // loadProgram()

    async reloadProgramWorkflows() {
      const programTargets = await this.getAllProgramTargets({
        program: this.programId,
      });
      const workflowIds = chain(programTargets)
        .map('workflow')
        .concat(get(this.program, 'workflow'))
        .concat(
          map(programTargets, 'defaultWorkflow'),
        )
        .uniq()
        .compact()
        .value();

      return this.reloadWorkflows(workflowIds);
    }, // reloadProgramWorkflows()

    jumpToTimestamp(timestamp) {
      if (this.edit || !timestamp) {
        return;
      }
      this.$emit('seek', timestamp);
    }, // jumpToTimestamp()

    getDurationBadge() {
      return this.formatDuration(this.currentSessionProgram.data.duration);
    },

    showTargetBadge(target) {
      return target.hasData;
    }, // showTargetBadge()

    showBelowMinTrialsBadge(target) {
      return target.isBelowMinTrials;
    }, // showBelowMinTrialsBadge()

    canDeleteTargetData(row) {
      if (this.isIntervalSubTarget(row)) {
        return this.edit && row.hasData;
      }
      return this.edit && row.hasData && (!['taskAnalysis', 'targetSet', 'interval'].includes(row.dataType));
    }, // canDeleteTargetData()

    getBadgeMetric(target) {
      if (this.badgeMetrics[target.id]) {
        return this.badgeMetrics[target.id];
      }

      const badgeMetric = this.getTargetMetric(target, this.program);

      // Cache badge format for quicker re-calcuation
      this.badgeMetrics[target.id] = badgeMetric;

      return badgeMetric;
    }, // getBadgeMetric()

    getBadge(row) {
      if (!this.currentSessionProgram.data) {
        return '';
      }

      if (this.checkIfTargetIsRating(row)) {
        return formatRatingScore(row.aggregatedData?.percentage);
      }

      const metric = this.getBadgeMetric(row.target);
      switch (metric) {
        case 'percentCorrect': {
          let val;
          if (row.aggregatedData.percentage === null) {
            val = '-';
          } else {
            val = Math.round(row.aggregatedData.percentage);
          }
          return this.$t('measurementMetrics.percentCorrect.format', { val });
        }
        case 'frequency': {
          return `${row.aggregatedData.successes} / ${row.aggregatedData.trials}`;
        }
        case 'totalDuration':
          if (row.aggregatedData.successfulDuration === row.aggregatedData.duration) {
            return `${this.formatDuration(row.aggregatedData.successfulDuration, false)}`;
          }
          return `${
            this.formatDuration(row.aggregatedData.successfulDuration, false)
          } / ${
            this.formatDuration(row.aggregatedData.duration, false)
          }`;
        case 'minDuration':
          if (row.aggregatedData.successfulMinDuration === row.aggregatedData.minDuration) {
            return `${this.formatDuration(row.aggregatedData.successfulMinDuration, false)}`;
          }
          return `${
            this.formatDuration(row.aggregatedData.successfulMinDuration, false)
          } / ${
            this.formatDuration(row.aggregatedData.minDuration, false)
          }`;
        case 'maxDuration':
          if (row.aggregatedData.successfulMaxDuration === row.aggregatedData.maxDuration) {
            return `${this.formatDuration(row.aggregatedData.successfulMaxDuration, false)}`;
          }
          return `${
            this.formatDuration(row.aggregatedData.successfulMaxDuration, false)
          } / ${
            this.formatDuration(row.aggregatedData.maxDuration, false)
          }`;
        case 'avgDuration':
          if (row.aggregatedData.successfulAvgDuration === row.aggregatedData.avgDuration) {
            return `${this.formatDuration(row.aggregatedData.successfulAvgDuration, false)}`;
          }
          return `${
            this.formatDuration(row.aggregatedData.successfulAvgDuration, false)
          } / ${
            this.formatDuration(row.aggregatedData.avgDuration, false)
          }`;

        case 'ratePerHour':
        case 'ratePerMinute':
          return formatRate(row.aggregatedData.successes, row.aggregatedData.duration, metric);

        default:
      }

      if ((get(row, 'dataType') === 'rate')) {
        return this.formatFrequency(
          row.aggregatedData.successes,
          row.aggregatedData.duration,
        );
        // return row.aggregatedData;
      }

      if (this.isFrequencyData) {
        return this.formatFrequency(
          row.aggregatedData.successes,
          this.currentSessionProgram.data.duration,
        );
      }
      if (this.isDurationTarget(row)) {
        if (row.aggregatedData.successfulDuration === row.aggregatedData.duration) {
          return `${this.formatDuration(row.aggregatedData.successfulDuration, false)}`;
        }
        return `${
          this.formatDuration(row.aggregatedData.successfulDuration, false)
        } / ${
          this.formatDuration(row.aggregatedData.duration, false)
        }`;
      }

      return `${row.aggregatedData.successes} / ${row.aggregatedData.trials}`;
    }, // getBadge()

    getBadgeIcon(row) {
      const metric = this.getBadgeMetric(row.target);
      return measurementMetrics.find((m) => m.id === metric)?.icon || '';
    }, // getBadgeIcon()

    getBadgeLabel(row) {
      const template = this.targetPromptLevelTemplate(row);
      if (template?.outcomeMeasurement === OUTCOME_MEASUREMENT_TYPES.RATING_SCALE) {
        return this.$t('sessions.trialOverview.badgeLabelAverage');
      }
      const metric = this.getBadgeMetric(row.target);
      return measurementMetrics.find((m) => m.id === metric)?.label || '';
    }, // getBadgeLabel()

    isBadgeLabelMissing(target) {
      const isCached = Object.keys(this.badgeMetrics).includes(target.id);
      return !isCached || !this.badgeMetrics[target.id];
    },

    canDelete(trial) {
      return !isEmpty(trial);
    }, // canDelete()

    targetPromptLevelTemplate(target) {
      const activeTargetPromptLevelTemplate = this.getActivePromptLevelTemplate(target.promptLevelTemplate);

      if (activeTargetPromptLevelTemplate) {
        return activeTargetPromptLevelTemplate;
      }

      const activeParentTargetPromptLevelTemplate = this.getParentDefaultPromptLevelTemplate(target.id);

      if (activeParentTargetPromptLevelTemplate) {
        return activeParentTargetPromptLevelTemplate;
      }

      return this.getActivePromptLevelTemplate(this.program.promptLevelTemplate);
    }, // targetPromptLevelTemplate()

    getParentDefaultPromptLevelTemplate(targetId) {
      const parentDefaultPromptLevelTemplate = chain(this.data)
        .find({ id: chain(this.data).find({ id: targetId }).get('parentTarget', null).value() })
        .get('defaultPromptLevelTemplate')
        .value();

      return this.getActivePromptLevelTemplate(parentDefaultPromptLevelTemplate);
    }, // getParentDefaultPromptLevelTemplate()

    getActivePromptLevelTemplate(promptLevelTemplateId) {
      return chain(this.activePromptLevels)
        .find({ id: promptLevelTemplateId })
        .value();
    }, // getActivePromptLevelTemplate()

    hasMultiplePromptLevels(target) {
      return size(this.getPromptLevels(target)) !== 1;
    }, // hasMultiplePromptLevels()

    getPromptLevels(target) {
      return chain(this.targetPromptLevelTemplate(target))
        .get('promptLevels', [])
        .value();
    }, // getPromptLevels()

    getStyleForTrialData(trial) {
      if (isEmpty(trial)) {
        return '';
      }

      // When trial data is an array, we should pick the trial item with a score value if it exists
      const preparedTrial = isArray(trial)
        ? (chain(trial).reject(({ scoreValue }) => isUndefined(scoreValue)).minBy('time').value() || {})
        : trial;

      let color = this.getPromptLevelColor(preparedTrial.promptLevelDetails, preparedTrial.scoreValue);

      let borderColor = colors.lighten(color, -20);
      const trialTime = isArray(trial)
        ? chain(trial).minBy('time').get('time', -1).value()
        : get(trial, 'time', -1);

      if ((this.session.hasTimecodedData)
        && (
          (trialTime <= this.fromTime)
          || (trialTime > this.time)
        )
      ) {
        color = colors.lighten(color, 85);
        borderColor = colors.lighten(borderColor, 85);
      }

      if (trial?.phantomTrial) {
        color = colors.lighten(color, -15);
        borderColor = colors.lighten(borderColor, -15);
      }

      return `background-color:${color};border-color:${borderColor}`;
    }, // getStyleForTrialData()

    getClassForTrialData(trial, parentTargetDataType, isTaskAnalysisTrial, target) {
      if (this.showEditMenu(trial, parentTargetDataType, isTaskAnalysisTrial, target)) {
        return 'trial cursor-pointer';
      }

      if (isEmpty(trial)) {
        return '';
      }

      return 'trial';
    }, // getClassForTrialData()

    getEditPromptItemClass(menuItemPromptLevel, dataPromptLevel) {
      return menuItemPromptLevel.id === dataPromptLevel.promptLevel ? 'text-bold' : '';
    }, // getEditPromptItemClass()

    getTargetWorkflow(target) {
      const parentRow = this.data.find((row) => row.id === target.parentTarget);
      const parentTarget = parentRow?.target || null;

      return this.getAdjustedWorkflow(this.workflowStatuses, this.program, target, parentTarget);
    }, // getTargetWorkflow()

    canAddData(target) {
      if (this.isIntervalSubTarget(target)) {
        return true;
      }
      return !this.getTargetDataType(target, this.program)?.hasSubTargets;
    }, // canAddData()

    hasTrialData(trials, trialIdx) {
      return !isEmpty(trials[trialIdx]);
    // Object.keys(props.row.trials[col.trial - 1]).length !== 0
    },

    onShowInfo() {
      if (this.canShowInfo) {
        this.openComponentDialog(ProgramInfo, { program: this.program });
      }
    },

    /**
     * A behavior occurrence stores only the id of the skill target it happened during, and that
     * target belongs to another program in the session. Programs without occurrences read nothing.
     */
    async loadAttachedTargetLabels() {
      const attachedTargetIds = new Set(
        (this.currentSessionProgram?.data?.data ?? [])
          .filter(isBehaviorOccurrence)
          .map(({ attachedToTarget }) => attachedToTarget),
      );
      if (attachedTargetIds.size === 0) {
        return {};
      }

      const targetsPerProgram = await Promise.all(
        this.sessionProgramIds.map((program) => this.getAllProgramTargets({ program })),
      );

      return Object.fromEntries(targetsPerProgram
        .flat()
        .filter(({ id }) => attachedTargetIds.has(id))
        .map(({ id, label }) => [id, label]));
    }, // loadAttachedTargetLabels()

    /**
     * The behavior occurrences recorded during this grid's targets, grouped into one square per save.
     * Only a Skill Acquisition grid shows them, and not while editing: they belong to their Behavior
     * Tracking program, so they are neither editable nor counted here.
     */
    async loadOccurrenceSlots() {
      if (this.edit || this.program?.type !== PROGRAM_TYPE_SKILL_ACQUISITION) {
        return [];
      }

      const rowTargetIds = new Set(this.data.map(({ id }) => id));
      const occurrences = this.sessionBehaviorOccurrences
        .filter(({ attachedToTarget }) => rowTargetIds.has(attachedToTarget));
      if (occurrences.length === 0) {
        return [];
      }

      const behaviorPrograms = [...new Set(occurrences.map(({ behaviorProgram }) => behaviorProgram))];
      const behaviorTargets = await Promise.all(
        behaviorPrograms.map((program) => this.getAllProgramTargets({ program })),
      );
      const behaviorLabels = Object.fromEntries(behaviorTargets.flat().map(({ id, label }) => [id, label]));

      return toOccurrenceSlots(
        occurrences.map((occurrence) => ({ ...occurrence, outcome: this.getOccurrenceOutcome(occurrence) })),
        behaviorLabels,
      );
    }, // loadOccurrenceSlots()

    /** The answered prompt level's label, found as analyzeTargetData finds a trial's. */
    getOccurrenceOutcome(occurrence) {
      if (!occurrence.promptLevel) {
        return null;
      }
      const templateId = occurrence.promptLevelTemplate || occurrence.behaviorProgramPromptLevelTemplate;

      // The store keeps templates keyed by id, not as a list.
      return Object.values(this.promptLevelTemplates ?? {})
        .find(({ id }) => id === templateId)
        ?.promptLevels?.find(({ id }) => id === occurrence.promptLevel)
        ?.trialOutcome ?? null;
    }, // getOccurrenceOutcome()

    getTrialTimes() {
      const times = {};
      (this.currentSessionProgram?.data?.data ?? []).forEach(({ trial, time }) => {
        if (trial && Number.isFinite(time)) {
          times[trial] = Math.min(times[trial] ?? time, time);
        }
      });
      return times;
    }, // getTrialTimes()

    isOccurrenceRow(slot, row) {
      return slot?.attachedToTarget === row.id;
    }, // isOccurrenceRow()

    getClassForOccurrence(slot, row) {
      if (!this.isOccurrenceRow(slot, row)) {
        return '';
      }
      return this.isOutsideTimeRange(slot.time)
        ? 'trial behavior-occurrence behavior-occurrence--outside-time'
        : 'trial behavior-occurrence';
    }, // getClassForOccurrence()

    isOutsideTimeRange(time) {
      return this.session.hasTimecodedData && ((time <= this.fromTime) || (time > this.time));
    }, // isOutsideTimeRange()

    getOccurrenceTooltip(slot) {
      const tally = (text, count) => (count > 1 ? `${text} ×${count}` : text);
      const lines = slot.behaviors.map(({
        programName, label, count, outcomes,
      }) => {
        const line = this.$t('sessions.trialOverview.textBehaviorOccurrenceLine', {
          program: programName,
          behavior: label,
        });
        if (outcomes.length === 0) {
          return escapeHtml(tally(line, count));
        }
        const answers = outcomes.map((outcome) => tally(outcome.label, outcome.count)).join(', ');
        return escapeHtml(`${line} — ${answers}`);
      });
      const heading = `${this.$t('sessions.trialOverview.textBehaviorOccurrence')} (${this.formatDuration(slot.time)})`;

      return [escapeHtml(heading), ...lines].join('<br />');
    }, // getOccurrenceTooltip()

    async mapTargetsAndOutcomes() {
      const selectedTargets = chain(this.currentSessionProgram)
        .get('selectedTargets')
        .value();

      this.data = await this.analyzeTargetData(
        this.session,
        this.program,
        get(this.currentSessionProgram, 'data', {}),
        this.edit,
        selectedTargets,
      );
      this.attachedTargetLabels = await this.loadAttachedTargetLabels();

      let columns = [
        {
          name: 'targetLabel',
          label: this.programName,
          align: 'left',
          classes: 'truncate-label-300',
        },
        {
          name: 'aggregatedData',
          label: '',
        }];

      let programModeHeaders = [];
      let maxTrial = chain(this.currentSessionProgram)
        .get(['data', 'data'])
        .maxBy('trial')
        .get('trial', 0)
        .value();

      if (!maxTrial) {
        const sessionProgramTrials = get(this.currentSessionProgram, ['data', 'data'], []);
        const durationOrFrequencyTargetIds = new Set(
          (this.data || [])
            .filter(({ target }) => ['duration', 'frequency'].includes(getTargetType(target, this.program)))
            .map(({ id }) => id),
        );

        if (durationOrFrequencyTargetIds.size && sessionProgramTrials.length) {
          maxTrial = chain(sessionProgramTrials)
            .filter((trial) => trial?.phantomTrial === true && durationOrFrequencyTargetIds.has(trial.target))
            .countBy('target')
            .values()
            .max()
            .defaultTo(0)
            .value();
        }
      }

      if (maxTrial) {
        // Columns start with the label of the target and then each trial
        columns = chain(columns)
          .concat(
            chain(range(1, maxTrial + 1))
              .map((trial) => ({
                name: `trial${trial}`,
                label: `#${trial}`,
                trial,
                isTaskAnalysisTrial: this.isTaskAnalysisProgram
                  || (
                    chain(this.data)
                      .map((target) => (
                        (target.parentTargetDataType === 'taskAnalysis')
                        && !chain(target).get(['trials', trial - 1]).isEmpty().value()
                      ))
                      .some()
                      .value()
                  ),
                ...(this.programHasMode ? {
                  mode: chain(this.currentSessionProgram)
                    .get(['data', 'data'])
                    .find({ trial })
                    .get('mode', null)
                    .value(),
                } : {}),
              }))
              .value(),
          )
          .value();

        if (this.programHasMode) {
          programModeHeaders = reduce(columns, (result, col, idx) => {
            if (isEmpty(result) || (col.mode || null) === null || result[result.length - 1].mode !== col.mode) {
              result.push(
                {
                  id: col.name,
                  mode: col.mode || null,
                  label: this.getProgramModeLabel(col),
                  color: this.getProgramModeBgColor(col),
                  count: 1,
                },
              );
            } else {
              result[result.length - 1].count += 1;
            }
            return result;
          }, []);
        }
      }

      const occurrenceSlots = await this.loadOccurrenceSlots();
      if (occurrenceSlots.length > 0) {
        const [labelColumn, aggregatedColumn, ...trialColumns] = columns;
        columns = [
          labelColumn,
          aggregatedColumn,
          ...interleaveOccurrenceColumns(trialColumns, this.getTrialTimes(), occurrenceSlots),
        ];
      }

      if (this.edit) {
        this.initData();
        columns = concat(columns, {
          name: `trial${maxTrial + 1}`,
          label: '',
          trial: maxTrial + 1,
        });
      }
      this.programModeHeaders = programModeHeaders;
      this.columns = columns;
    },

    getProgramModeLabel(col) {
      if (!col?.mode) {
        return '';
      }
      return get(programTypes, [this.program.type, 'modes', col.mode, 'label'], '');
    },

    getProgramModeBgColor(col) {
      if (!col?.mode) {
        return '';
      }
      return get(programTypes, [this.program.type, 'modes', col.mode, 'color'], '');
    },

    supervisionNotesTitle(name) {
      if (name) {
        return this.$t('sessions.trialOverview.supervisionNotesBy', { supervisedByName: name });
      }
      return this.$t('sessions.trialOverview.supervisionNotes');
    },

    isIntervalSubTarget(target) {
      const parentTarget = this.data.find((row) => row.id === target.parentTarget);
      return parentTarget?.dataType === 'interval';
    },

  },

};
</script>

<style scoped>
.q-table {
  border-collapse: collapse;
}

.q-table td.trial {
  border-style: solid;
  border-width: 1px;
}

/* Behavior tracking reads as different from a trial at a glance, not only by color. */
.q-table td.behavior-occurrence {
  position: relative;
  background-color: var(--app-warning);
}

/* A layer of its own, as no token carries the hatching's translucency. */
.q-table td.behavior-occurrence::before {
  content: '';
  position: absolute;
  inset: 0;
  background-image: repeating-linear-gradient(
    45deg,
    transparent 0 4px,
    var(--app-shadow) 4px 8px
  );
  opacity: 0.18;
  pointer-events: none;
}

.q-table td.behavior-occurrence--outside-time {
  opacity: 0.15;
}

.q-table td.sub-target-1 {
  padding-left:32px;
}

.q-table td.sub-target-2 {
  padding-left:48px;
}

.q-table td.sub-target-3 {
  padding-left:64px;
}

.q-table td.sub-target-4 {
  padding-left:80px;
}

.q-table td.nostretch {
  width: 1%;
  white-space: nowrap;
}

.prompt-level {
  border-width: 0 5px;
  border-style: solid;
}

.sticky-col th:first-child,
.sticky-col td:first-child {
  position: sticky;
  left: 0;
  z-index: 1;
  background-color: #fff;
}

.no-data-title {
  font-weight: 500;
  font-size: 12px;
  border-bottom: solid rgba(0, 0, 0, 0.12) 1px;
}

.target-label-truncate {
  max-width: 200px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

@media only screen and (min-width: 600px) {
  .target-label-truncate {
    max-width: 300px;
  }
}

</style>
<style>

table.tooltip-table, table.tooltip-table tr, table.tooltip-table td {
  border:solid #cacaca 1px;
  border-collapse: collapse;
}

table.tooltip-table td {
  padding: 4px;
}

.lower-height .custom-editor .ProseMirror {
  min-height: unset;
}

</style>
